import {
  ASSIGN_BOARD_RUNNER,
  UNASSIGN_BOARD_RUNNER,
  GET_BOARD_RUNNERS,
  GET_BOARD_ASSIGNMENTS,
  ASSIGN_BOARD_ASSIGNMENT,
  UNASSIGN_BOARD_ASSIGNMENT
} from "constants/apiEndpoints";

import { AXIOS_METHODS } from "constants/common";
import { useAxios } from "hooks";
import { useCallback, useEffect } from "react";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";

const useBoardRunner = ({ boardDate, facilityId, handleRefreshProvidersList }) => {
  const user = useSelector((state) => state?.user?.user);
  const isProvider = user?.role == 'provider';
  const {
    triggerApi: triggerGetBoardRunnersApi,
    cancelRequest: cancelGetBoardRunnersApi,
    loading: getBoardRunnersApiLoading,
    response: boardRunners,
  } = useAxios({
    method: AXIOS_METHODS.GET,
    url: GET_BOARD_RUNNERS,
    onInit: false,
  });

  const {
    triggerApi: triggerAssignBoardRunnerApi,
    loading: assignBoardRunnerApiLoading,
  } = useAxios({
    method: AXIOS_METHODS.POST,
    url: ASSIGN_BOARD_RUNNER,
    onInit: false,
  });

  const {
    triggerApi: triggerUnassignBoardRunnerApi,
    loading: unassignBoardRunnerApiLoading,
  } = useAxios({
    method: AXIOS_METHODS.POST,
    url: UNASSIGN_BOARD_RUNNER,
    onInit: false,
  });

  const {
    triggerApi: triggerGetBoardAssignmentsApi,
    cancelRequest: cancelGetBoardAssignmentsApi,
    loading: getBoardAssignmentsApiLoading,
    response: boardAssignments,
  } = useAxios({
    method: AXIOS_METHODS.GET,
    url: GET_BOARD_ASSIGNMENTS,
    onInit: false,
  });

  const {
    triggerApi: triggerAssignBoardAssignmentApi,
    loading: assignBoardAssignmentApiLoading,
  } = useAxios({
    method: AXIOS_METHODS.POST,
    url: ASSIGN_BOARD_ASSIGNMENT,
    onInit: false,
  });

  const {
    triggerApi: triggerUnassignBoardAssignmentApi,
    loading: unassignBoardAssignmentApiLoading,
  } = useAxios({
    method: AXIOS_METHODS.POST,
    url: UNASSIGN_BOARD_ASSIGNMENT,
    onInit: false,
  });

  const handleAssignBoardRunner = async (item) => {
    // Handle both single provider (drag/drop) and multiple providers (dropdown)
    const providerIds = item.providerIds || [item.providerId];

    const response = await triggerAssignBoardRunnerApi({
      data: {
        providerId: providerIds,
        facilityId,
        assignmentDate: boardDate.toISOString(),
      },
    });
    if (response.success) {
      getBoardRunners(boardDate);
      handleRefreshProvidersList();
    }
  };

  const handleUnassignBoardRunner = async (provider_id = null) => {
    const response = await triggerUnassignBoardRunnerApi({
      data: {
        assignmentDate: boardDate.toISOString(),
        facilityId,
        provider_id,
      },
    });
    if (response?.success) {
      getBoardRunners(boardDate);
      handleRefreshProvidersList();
    }
  };

  const handleAssignBoardAssignment = async (item) => {
    // Handle both single provider (drag/drop) and multiple providers (dropdown)
    const providerIds = item?.selectedProviders?.providerIds || [item.providerId];
    const type = item?.type;

    const response = await triggerAssignBoardAssignmentApi({
      data: {
        providerId: providerIds,
        facilityId,
        assignmentDate: boardDate.toISOString(),
        type
      },
    });
    if (response.success) {
      handleRefreshProvidersList();
      getBoardAssignments(boardDate);
    }
  };

  const handleUnassignBoardAssignment = async ({providerId, type}) => {
    const response = await triggerUnassignBoardAssignmentApi({
      data: {
        assignmentDate: boardDate.toISOString(),
        facilityId,
        providerId,
        type
      },
    });
    if (response?.success) {
      handleRefreshProvidersList();
      getBoardAssignments(boardDate);
    }
  };

  const getBoardRunners = useCallback(
    async (boardDate) => {
      await triggerGetBoardRunnersApi({
        url: GET_BOARD_RUNNERS,
        params: {
          assignmentDate: boardDate.toISOString(),
          facilityId,
        },
      });
    },
    [triggerGetBoardRunnersApi, facilityId],
  );

  const getBoardAssignments = useCallback(
    async (boardDate) => {
      await triggerGetBoardAssignmentsApi({
        url: GET_BOARD_ASSIGNMENTS,
        params: {
          assignmentDate: boardDate.toISOString(),
          facilityId,
        },
      });
    },
    [triggerGetBoardAssignmentsApi, facilityId],
  );

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: "item-on-event",
      drop: (item) => {
        // Get existing provider IDs from boardRunners
        const existingProviderIds = boardRunners?.[0]?.provider_id || [];
        
        // Get new provider IDs from dropped item
        const newProviderIds = item.providerIds || [item.providerId];
        
        // Merge existing and new provider IDs, avoiding duplicates
        const mergedProviderIds = [...new Set([...existingProviderIds, ...newProviderIds])];
        
        // Create merged item with combined provider IDs
        const mergedItem = {
          ...item,
          providerIds: mergedProviderIds
        };
        
        handleAssignBoardRunner(mergedItem);
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [facilityId, boardRunners],
  );

  // ---- Custom hook for any assignment type ----
  const useAssignmentDrop = ({ type, providerIds }) => {
    const [{ isOver: isOverAssignment }, assignmentDropRef] = useDrop(
      () => ({
        accept: "item-on-event",
        drop: (item) => {
          const newProviderIds = item.providerIds || [item.providerId];
          const mergedProviderIds = [...new Set([...providerIds, ...newProviderIds])];

          handleAssignBoardAssignment({
            type,
            selectedProviders: { providerIds: mergedProviderIds, providerNames: [] },
          });
        },
        collect: (monitor) => ({ isOver: monitor.isOver() }),
      }),
      [providerIds]
    );

    return { assignmentDropRef, isOverAssignment };
    };

  useEffect(() => {
    getBoardRunners(boardDate);
    return () => cancelGetBoardRunnersApi();
  }, [boardDate, getBoardRunners, cancelGetBoardRunnersApi]);

  useEffect(() => {
    getBoardAssignments(boardDate);
    return () => cancelGetBoardAssignmentsApi();
  }, [boardDate, getBoardAssignments, cancelGetBoardAssignmentsApi]);

  return {
    dropRef,
    useAssignmentDrop,
    isOver,
    handleAssignBoardRunner,
    handleUnassignBoardRunner,
    getBoardRunnersApiLoading,
    getBoardAssignmentsApiLoading,
    boardRunners,
    boardAssignments,
    assignBoardRunnerApiLoading,
    unassignBoardRunnerApiLoading,
    handleAssignBoardAssignment,
    handleUnassignBoardAssignment,
    assignBoardAssignmentApiLoading,
    unassignBoardAssignmentApiLoading,
    isProvider
  };
};

export default useBoardRunner;
